import { useRef, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Paragraph, YStack } from 'tamagui';
import { Screen, SkeletonList } from '@mrrmlab/ui';
import { ShoppingListScreen, type OcrModelId } from '@mrrmlab/feature-shopping-list';
import { NotesListScreen, TodoListScreen, useList } from '@mrrmlab/feature-lists';
import { ocrImageStore } from '../ocr-image-store';

export function ListDetailRoute() {
  const { listId } = useParams({ from: '/lists/$listId' });
  const navigate = useNavigate();
  const listQuery = useList(listId);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pendingModelRef = useRef<OcrModelId | undefined>(undefined);
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  const back = () => navigate({ to: '/lists' });

  const handleCameraPress = (model: OcrModelId) => {
    pendingModelRef.current = model;
    setShowSourcePicker(true);
  };

  const handleSourceSelect = (source: 'camera' | 'gallery') => {
    setShowSourcePicker(false);
    if (source === 'camera') {
      cameraInputRef.current?.click();
    } else {
      galleryInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    ocrImageStore.set(file);
    void navigate({
      to: '/lists/$listId/image-preview',
      params: { listId },
      search: { model: pendingModelRef.current },
    });
  };

  if (listQuery.isPending) {
    return (
      <Screen>
        <SkeletonList count={8} />
      </Screen>
    );
  }

  if (listQuery.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">
          Failed to load list: {listQuery.error.message}
        </Paragraph>
      </Screen>
    );
  }

  const type = listQuery.data.type;
  return (
    <>
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} />
      <input ref={galleryInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
      {showSourcePicker && (
        <>
          <div
            onClick={() => setShowSourcePicker(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50 }}
          />
          <YStack
            style={{ position: 'fixed', bottom: 24, left: 16, right: 16, zIndex: 51 }}
            gap="$2"
            padding="$4"
            backgroundColor="$background"
            borderRadius="$4"
            borderWidth={1}
            borderColor="$borderColor"
          >
            <Button size="$5" onPress={() => handleSourceSelect('camera')}>Kamera</Button>
            <Button size="$5" onPress={() => handleSourceSelect('gallery')}>Aus Datei wählen</Button>
            <Button size="$5" chromeless onPress={() => setShowSourcePicker(false)}>Abbrechen</Button>
          </YStack>
        </>
      )}
      {type === 'shopping' ? (
        <ShoppingListScreen
          listId={listId}
          onBack={back}
          onCameraPress={handleCameraPress}
          onTextImportPress={() =>
            void navigate({ to: '/lists/$listId/text-import', params: { listId } })
          }
          onEnterShoppingMode={() =>
            navigate({ to: '/lists/$listId/einkaufen', params: { listId } })
          }
        />
      ) : type === 'notes' ? (
        <NotesListScreen listId={listId} onBack={back} />
      ) : type === 'todo' ? (
        <TodoListScreen listId={listId} onBack={back} />
      ) : (
        <Screen>
          <Paragraph opacity={0.7}>
            List type `{type}` has no detail screen yet.
          </Paragraph>
        </Screen>
      )}
    </>
  );
}
